// src/components/templates/layout/Layout.jsx
import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { CssBaseline, useMediaQuery, useTheme } from '@mui/material';
import Header from '../../organisms/layout/Header';
import Sidebar from '../../organisms/layout/Sidebar';
import PageLayout from '../PageLayout/PageLayout';
import { AppBarActionsProvider } from '../../../context/AppBarActionsContext';

const drawerWidth = 240;
const miniDrawerWidth = 64;

/**
 * Layout - Componente organismo para implementar o layout principal da aplicação
 *
 * Este componente usa o template PageLayout do Atomic Design e
 * implementa a estrutura específica da aplicação com Header, Sidebar e Outlet.
 * Segue o princípio do Atomic Design como um "organismo".
 */
const Layout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // No mobile: sidebar começa fechada
  // No desktop: sidebar começa aberta
  const [open, setOpen] = useState(!isMobile);

  const toggleDrawer = () => {
    setOpen(!open);
  };

  // Fechar sidebar (usado pelo drawer temporário no mobile)
  const handleCloseSidebar = () => {
    if (isMobile) {
      setOpen(false);
    }
  };

  // Conteúdo a ser passado para o PageLayout
  const headerContent = <Header toggleDrawer={toggleDrawer} sidebarOpen={open} />;
  
  const mainContent = <Outlet />;

  // Classe CSS customizada para ajustar a largura e margens com base no estado do drawer
  const customClass = `layout-with-${open ? 'open' : 'closed'}-sidebar`;

  // Estilo inline para o conteúdo principal - Apenas para garantir transição suave
  // Isso será aplicado via className no PageLayout
  const dynamicStyles = `
    .layout-with-open-sidebar .page-layout__content,
    .layout-with-closed-sidebar .page-layout__content {
      transition: margin 0.3s ease, width 0.3s ease;
    }
    
    .page-layout__header {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
    }
    
    .page-layout__main {
      box-sizing: border-box;
      height: 100vh;
      padding-top: 80px;
      overflow: hidden;
    }
    
    @media (max-width: 768px) {
      .layout-with-open-sidebar .page-layout__content,
      .layout-with-closed-sidebar .page-layout__content {
        width: 100%;
        margin-left: 0;
      }
    }
  `;

  return (
    <>
      <CssBaseline />
      <style>{dynamicStyles}</style>
      <AppBarActionsProvider>
        <PageLayout
          header={headerContent}
          sidebar={<Sidebar open={open} drawerWidth={drawerWidth} miniDrawerWidth={miniDrawerWidth} isMobile={isMobile} onClose={handleCloseSidebar} />}
          content={mainContent}
          className={customClass}
        />
      </AppBarActionsProvider>
    </>
  );
};

export default Layout;
